import Swal from 'sweetalert2'

/**
 * Minimum Order Quantity (MOQ) enforcement for the quantity inputs.
 *
 * MOQ is the product's `units_per_pack` (1 = no restriction). A size's quantity
 * must be 0, a multiple of the pack size, or the full quantity available by the
 * order's ship date (so a buyer can take the whole partial top pack instead of
 * being bumped to a multiple that would backorder). Seller-side users (admin /
 * rep) bypass entirely. The multiple rule is waived when a size has less stock
 * than one full pack. The backend re-checks against the order's ship date and is
 * the source of truth; this mixin only provides the inline UX (step buttons +
 * snap-up + popup).
 *
 * Host components must expose an `orderItemProduct` (with `units_per_pack`,
 * `is_prebooked`, `name`) and pass size objects carrying `quantity_available`
 * and `user_given_quantity`.
 */
export default {
  methods: {
    /**
     * Pack size for the current product. 1 = no restriction.
     */
    moqForProduct () {
      return Number(this.orderItemProduct?.units_per_pack) || 1
    },
    /**
     * Seller-side users break packs for customers, so they bypass MOQ.
     */
    isMoqBypassUser () {
      const getters = this.$store?.getters || {}
      return Boolean(getters['auth/isAdmin'] || getters['auth/isRep'])
    },
    /**
     * Whether MOQ multiples must be enforced for this size. Enforcement is waived
     * when fewer than one full pack is reachable by the order's ship date. Uses
     * the backend-computed `moq_available` (ATS + POs on/before the ship date,
     * prebook included via date) when present, falling back to `quantity_available`
     * on screens that don't ship it (e.g. product listing). The backend MoqManager
     * applies the same rule and is the source of truth.
     */
    moqEnforcedForSize (productSize) {
      const moq = this.moqForProduct()
      if (moq <= 1 || this.isMoqBypassUser()) {
        return false
      }
      return this.moqAvailableFor(productSize) >= moq
    },
    /**
     * Stock reachable by the order's ship date for a size. Uses the backend-computed
     * `moq_available` (ATS + POs on/before the ship date) when present, falling back
     * to `quantity_available` on screens that don't ship it (e.g. product listing).
     */
    moqAvailableFor (productSize) {
      return Number(
        productSize?.moq_available != null ? productSize.moq_available : productSize?.quantity_available
      ) || 0
    },
    /**
     * Round a quantity up to the nearest valid stop. Valid stops are the full-pack
     * multiples and, when the next multiple would overshoot the stock available by
     * the ship date, the available quantity itself (the partial top pack). Values
     * already above availability are left to snap to a multiple — the exceed /
     * backorder flow owns them.
     */
    snapToPack (quantity, moq, available = 0) {
      const value = Number(quantity) || 0
      if (value <= 0) {
        return value
      }
      const nextMult = Math.max(moq, Math.ceil(value / moq) * moq)
      if (available > 0 && value <= available && nextMult > available) {
        return available
      }
      return nextMult
    },
    /**
     * Nearest valid stop strictly above `current` (for the "+" button): the next
     * full pack, or the available ceiling when that pack would overshoot it.
     */
    nextMoqValue (productSize, current) {
      const moq = this.moqForProduct()
      const value = Number(current) || 0
      if (!this.moqEnforcedForSize(productSize)) {
        return value + 1
      }
      const available = this.moqAvailableFor(productSize)
      const nextMult = Math.max(moq, (Math.floor(value / moq) + 1) * moq)
      if (value < available && nextMult > available) {
        return available
      }
      return nextMult
    },
    /**
     * Nearest valid stop strictly below `current` (for the "−" button): steps down
     * off the available ceiling to the highest full pack, then by whole packs.
     */
    prevMoqValue (productSize, current) {
      const moq = this.moqForProduct()
      const value = Number(current) || 0
      if (!this.moqEnforcedForSize(productSize)) {
        return Math.max(0, value - 1)
      }
      const available = this.moqAvailableFor(productSize)
      if (value > available && available % moq !== 0) {
        return available
      }
      const prevMult = (Math.ceil(value / moq) - 1) * moq
      return Math.max(0, prevMult)
    },
    /**
     * A size the user is still shaping toward a valid value: MOQ-enforced and a
     * positive non-multiple that isn't the full available quantity. Used to defer
     * the snap/persist while typing so a multi-digit multiple (e.g. 75 for MOQ 25)
     * isn't rewritten after the first digit; the snap runs on commit (blur / Enter)
     * instead. The available ceiling is a committed valid value, so it persists.
     */
    isMoqPartial (productSize) {
      if (!this.moqEnforcedForSize(productSize)) {
        return false
      }
      const moq = this.moqForProduct()
      const value = Number(productSize.user_given_quantity) || 0
      if (value <= 0 || value % moq === 0) {
        return false
      }
      return value !== this.moqAvailableFor(productSize)
    },
    showMoqDialog (moq, adjusted) {
      Swal.fire({
        icon: 'warning',
        title: 'Minimum Order Quantity',
        text: `Minimum order quantities for ${this.orderItemProduct?.name || 'this product'} are in multiples of ${moq}, or the full quantity available. Quantity adjusted to ${adjusted}.`,
        confirmButtonText: 'OK',
        // The product-detail modal sits at z-index 99999; keep this dialog above it.
        didOpen: () => {
          const container = document.querySelector('.swal2-container')
          if (container) {
            container.style.zIndex = '100001'
          }
        }
      })
    },
    /**
     * Snap a size's quantity to a valid pack multiple when needed. Mutates
     * `productSize.user_given_quantity` and pops a dialog on adjustment.
     * Returns the resulting quantity (unchanged when no snap is required).
     */
    applyMoqSnap (productSize) {
      const current = Number(productSize.user_given_quantity) || 0
      if (!this.moqEnforcedForSize(productSize)) {
        return current
      }
      const moq = this.moqForProduct()
      const available = this.moqAvailableFor(productSize)
      // Multiples and "take the full available quantity" are already valid.
      if (current > 0 && current % moq !== 0 && current !== available) {
        const snapped = this.snapToPack(current, moq, available)
        productSize.user_given_quantity = snapped
        this.showMoqDialog(moq, snapped)
        return snapped
      }
      return current
    }
  }
}
